import {
  Alert,
  Button,
  Checkbox,
  message,
  Select,
  type SelectProps,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  defaultGuestTemplateKey,
  type PermissionTemplate,
  permissionTemplatesApi,
  projectGuestTemplateKey,
  publicRoomGuestTemplateKey,
  type TemplateDefinition,
  type TemplateRules,
  type TemplateVersion,
} from '@/services/tuttiPermissionTemplatesApi';

const templateCopy: Record<string, { label: string; description: string }> = {
  [projectGuestTemplateKey]: {
    label: '项目游客',
    description:
      '激活版本影响所有已开启游客模式的项目。项目拥有者仅能调整服务端允许的项目权限；匿名浏览始终只读，不能获得任务写入、运行时或房间资源权限。仅发布草稿不会切换生效版本。',
  },
  [defaultGuestTemplateKey]: {
    label: '普通房间游客',
    description:
      '激活版本会影响新房间和已有的普通房间：房主可修改的权限优先使用房间显式设置，其余权限使用当前模板。取消某项的房主修改资格后，该项原有房间设置将不再生效。游客模式仍需房主开启；仅发布草稿不会切换生效版本。',
  },
  [publicRoomGuestTemplateKey]: {
    label: '公共房间游客',
    description:
      '激活版本会立即影响所有公共房间（含草稿、已下架的公共房间）中非房主游客的权限。公共房间房主不能调整任何一项；仅发布草稿不会切换生效版本。',
  },
};
function failure(error: unknown) {
  const status = (error as { response?: { status?: number } })?.response
    ?.status;
  if (status === 409)
    return '版本已变化或已发布，请刷新后重试；发布前请先登记全部权限。';
  if (status === 403) return '当前账号没有管理权限。';
  if (status === 400) return '模板包含尚未支持的游客权限，或配置不完整。';
  return '模板操作失败，请检查服务状态后重试。';
}
export function PermissionTemplatesPanel() {
  const [notice, context] = message.useMessage();
  const [definitions, setDefinitions] = useState<TemplateDefinition[]>([]);
  const [heads, setHeads] = useState<PermissionTemplate[]>([]);
  const [templateKey, setTemplateKey] = useState(defaultGuestTemplateKey);
  const [versions, setVersions] = useState<TemplateVersion[]>([]);
  const [rules, setRules] = useState<TemplateRules>();
  const [selected, setSelected] = useState<TemplateVersion>();
  const [nextVersion, setNextVersion] = useState('0');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const generation = useRef(0);
  const definition = definitions.find((item) => item.key === templateKey);
  const template = heads.find((item) => item.key === templateKey);
  const grantable = useMemo(
    () => new Set(definition?.grantablePermissions),
    [definition],
  );
  const ownerEditable = useMemo(
    () => new Set(definition?.ownerEditableScope),
    [definition],
  );
  const load = useCallback(async (key: string) => {
    const id = ++generation.current;
    setLoading(true);
    setError('');
    try {
      const [page, history] = await Promise.all([
        permissionTemplatesApi.list(),
        permissionTemplatesApi.versions(key),
      ]);
      if (id !== generation.current) return;
      const current = page.definitions?.find((item) => item.key === key);
      if (!current) {
        // Template definitions are required; an older server cannot manage them.
        setError('服务端尚未支持多模板管理，请先升级服务端。');
        return;
      }
      setDefinitions(page.definitions);
      setHeads(page.templates);
      setVersions(history.versions);
      setNextVersion(history.nextVersion);
      setSelected(undefined);
      setRules(current.initialRules);
    } catch (cause) {
      if (id === generation.current) setError(failure(cause));
    } finally {
      if (id === generation.current) setLoading(false);
    }
  }, []);
  useEffect(() => {
    void load(templateKey);
    return () => {
      generation.current++;
    };
  }, [load, templateKey]);
  const run = async (operation: () => Promise<unknown>, success: string) => {
    setSaving(true);
    try {
      await operation();
      notice.success(success);
      await load(templateKey);
    } catch (cause) {
      notice.error(failure(cause));
    } finally {
      setSaving(false);
    }
  };
  const more = async () => {
    const id = generation.current;
    setSaving(true);
    try {
      const page = await permissionTemplatesApi.versions(
        templateKey,
        nextVersion,
      );
      if (id === generation.current) {
        setVersions((current) => [...current, ...page.versions]);
        setNextVersion(page.nextVersion);
      }
    } catch (cause) {
      notice.error(failure(cause));
    } finally {
      setSaving(false);
    }
  };
  const ownerLabel =
    definition?.scopeType === 'collaboration_project' ? '项目拥有者' : '房主';
  const scopeLabel =
    definition?.scopeType === 'collaboration_project' ? '项目' : '房间';
  const immutable = selected?.state === 'published';
  const blocked = loading || saving || !!error;
  return (
    <Space orientation="vertical" size="large" style={{ width: '100%' }}>
      {context}
      <Select<string, NonNullable<SelectProps['options']>[number]>
        aria-label="游客模板"
        style={{ minWidth: 300 }}
        value={templateKey}
        disabled={saving}
        options={
          definitions.length
            ? [
                {
                  label: '房间（Room）',
                  options: definitions
                    .filter((item) => item.scopeType === 'room')
                    .map((item) => ({
                      value: item.key,
                      label: templateCopy[item.key]?.label ?? item.name,
                    })),
                },
                {
                  label: '项目（Project）',
                  options: definitions
                    .filter(
                      (item) => item.scopeType === 'collaboration_project',
                    )
                    .map((item) => ({
                      value: item.key,
                      label: templateCopy[item.key]?.label ?? item.name,
                    })),
                },
              ]
            : [
                defaultGuestTemplateKey,
                publicRoomGuestTemplateKey,
                projectGuestTemplateKey,
              ].map((key) => ({
                value: key,
                label: templateCopy[key]?.label ?? key,
              }))
        }
        onChange={(value) => setTemplateKey(value)}
      />
      <Alert
        type="info"
        showIcon
        title={`${templateCopy[templateKey]?.label ?? definition?.name ?? templateKey}模板决定这类${scopeLabel}游客继承的权限`}
        description={templateCopy[templateKey]?.description}
      />
      <Space wrap>
        <Typography.Text strong>
          {templateCopy[templateKey]?.label ?? definition?.name ?? templateKey}
          模板
        </Typography.Text>
        <Tag>
          {template?.activeVersion && template.activeVersion !== '0'
            ? `当前版本 v${template.activeVersion}`
            : '使用服务端默认模板'}
        </Tag>
        <Button
          onClick={() => void load(templateKey)}
          disabled={saving}
          loading={loading}
        >
          刷新
        </Button>
      </Space>
      {error && <Alert type="error" showIcon title={error} />}
      <Spin spinning={loading}>
        <Space orientation="vertical" style={{ width: '100%' }} size="middle">
          <Space wrap>
            <Select
              aria-label="模板版本"
              style={{ minWidth: 230 }}
              value={selected?.version ?? 'new'}
              disabled={blocked}
              options={[
                { value: 'new', label: '新建草稿' },
                ...versions.map((v) => ({
                  value: v.version,
                  label: `v${v.version} · ${v.state === 'draft' ? '草稿' : '已发布'}`,
                })),
              ]}
              onChange={(value) => {
                const version = versions.find((v) => v.version === value);
                setSelected(version);
                setRules(version?.rules ?? definition?.initialRules);
              }}
            />
            {nextVersion !== '0' && nextVersion !== '' && (
              <Button disabled={blocked} onClick={() => void more()}>
                加载更多版本
              </Button>
            )}
            {selected && <Tag>{immutable ? '已发布 · 内容冻结' : '草稿'}</Tag>}
          </Space>
          <Table
            rowKey="key"
            pagination={false}
            dataSource={Object.entries(rules?.permissions ?? {})
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([key, allowed]) => ({ key, allowed }))}
            columns={[
              {
                title: '权限',
                dataIndex: 'key',
                render: (key: string) => (
                  <Typography.Text code>{key}</Typography.Text>
                ),
              },
              {
                title: '默认允许',
                render: (_, row) => (
                  <Switch
                    aria-label={`${row.key} 默认允许`}
                    checked={row.allowed}
                    disabled={blocked || immutable || !grantable.has(row.key)}
                    onChange={(allowed) =>
                      setRules(
                        (current) =>
                          current && {
                            ...current,
                            permissions: {
                              ...current.permissions,
                              [row.key]: allowed,
                            },
                          },
                      )
                    }
                  />
                ),
              },
              {
                title: `${ownerLabel}可调整`,
                render: (_, row) => (
                  <Checkbox
                    aria-label={`${row.key} ${ownerLabel}可调整`}
                    checked={rules?.ownerEditablePermissions.includes(row.key)}
                    disabled={
                      blocked || immutable || !ownerEditable.has(row.key)
                    }
                    onChange={(event) => {
                      const checked = event.target.checked;
                      setRules(
                        (current) =>
                          current && {
                            ...current,
                            ownerEditablePermissions: checked
                              ? [
                                  ...current.ownerEditablePermissions.filter(
                                    (key) => key !== row.key,
                                  ),
                                  row.key,
                                ]
                              : current.ownerEditablePermissions.filter(
                                  (key) => key !== row.key,
                                ),
                          },
                      );
                    }}
                  />
                ),
              },
            ]}
          />
          <Space wrap>
            {!immutable && (
              <Button
                type="primary"
                disabled={blocked || !rules}
                onClick={() =>
                  rules &&
                  void run(
                    () =>
                      permissionTemplatesApi.save(
                        templateKey,
                        rules,
                        selected?.version,
                        selected?.revision,
                      ),
                    '草稿已保存',
                  )
                }
              >
                保存草稿
              </Button>
            )}
            {selected && (
              <Button
                disabled={blocked || !rules}
                onClick={() =>
                  rules &&
                  void run(
                    () => permissionTemplatesApi.save(templateKey, rules),
                    '已复制为新草稿',
                  )
                }
              >
                复制为新草稿
              </Button>
            )}
            {selected?.state === 'draft' && (
              <Button
                disabled={
                  blocked ||
                  JSON.stringify(rules) !== JSON.stringify(selected.rules)
                }
                onClick={() =>
                  void run(
                    () => permissionTemplatesApi.publish(selected),
                    '版本已发布，尚未切换生效模板',
                  )
                }
              >
                发布已保存的草稿
              </Button>
            )}
            {immutable && selected && template && (
              <Button
                type="primary"
                disabled={
                  blocked || template.activeVersion === selected.version
                }
                onClick={() =>
                  void run(
                    () => permissionTemplatesApi.activate(selected, template),
                    `模板已生效，相关${scopeLabel}均按继承规则更新`,
                  )
                }
              >
                设为生效默认模板
              </Button>
            )}
          </Space>
        </Space>
      </Spin>
    </Space>
  );
}
